<template>
<div id="app">

  <!-- header -->
  <HeaderHero />

  <!-- body -->
  <div class='section' id='body-div'>
    <div class='content'>

      <!-- wrapper for text overlay on charts !-->
      <div class='wrapper'>

        <!-- every div needs to be .inside-wrapper -->
        <Visuals class='viz-wrapper' :data="apiData" :screenWidth="screenWidth" :screenHeight="screenHeight" :isPortrait="isPortrait" :config="config" @loaded="initTriggers" />

        <!-- needs to be .content-wrapper -->
        <div class='content-wrapper'>
          <div class='columns is-centered'>
            <div class='column is-6'>
              <Content />
            </div>
          </div>
        </div>
      </div>


    </div>
  </div>

  <MyFooter />

  </div>
</template>

<script>
import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
gsap.registerPlugin(ScrollTrigger);

import debounce from 'lodash.debounce';
import { csv } from 'd3-fetch';

import HeaderHero from './components/HeaderHero.vue';
import Visuals from './components/Visuals.vue';
import Content from './components/Content.vue';
import MyFooter from './components/MyFooter.vue';

export default {
  name: 'App',
  components: {
    HeaderHero,
    Visuals,
    Content,
    MyFooter
  },
  data() {
    return {
      screenWidth: 0,
      screenHeight: 0,
      isPortrait: false,
      apiData: [],
      config: {},
    }
  },
  created() {
    window.addEventListener('resize', debounce(this.handleResize, 100
      ));
  },
  mounted() {
    this.getData();
    this.handleResize();
  },
  destroyed() {
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    handleResize() {
      const oldIsPortrait = this.isPortrait;
      const oldHeight = this.innerHeight;

      this.isPortrait = window.innerHeight > window.innerWidth;
      
      this.screenWidth = window.innerWidth;
      this.screenHeight = window.innerHeight;
    
      // save vh variable
      let vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);

      // if switch orientation, refresh scrollTrigger
      // if change screenHeight, refresh scrollTrigger
      if (this.isPortrait && (!oldIsPortrait)) {
        ScrollTrigger.refresh();
      } else if (this.screenHeight != oldHeight) {
        ScrollTrigger.refresh();
      }
    },
    async getData() {
      // load the CSV
      // const uri = '/clean_data.csv'
      const uri = 'https://firebasestorage.googleapis.com/v0/b/squirrel-census-2018.appspot.com/o/clean_data.csv?alt=media&token=398a0b4d-e59d-4fcb-b09d-c66c8637b6d9'
      const color_dict = {
        'G': 'Gray',
        'B': 'Black',
        'C': 'Cinnamon',
        'n': 'none'
      }

      let data = await csv(uri, function(d) {
        return {
          lon: +d.x - 73,
          lat: +d.y + 40,
          id: d.id,
          hectare: d.id.split('-')[0],
          day: parseInt(d.id.split('-')[2].slice(-2)),
          time: d.id.split('-')[1],
          color_primary: color_dict[d.color_primary],
          height: +d.height,
          age: +d.age === 0 ? 'Adult' : 'Juvenile',
        };
      })
      .then(data => {
        return data;
      });

      // save output
      this.apiData = data;
    },
    initTriggers() {
      this.pinMapAndGrid();

      this.triggerMapGridReveal();
      this.triggerMapCells();

      this.pinObservationsAndDescriptions();
      this.pinThisIsASquirrel();

      this.triggerRevealFirstSquirrel();
      this.triggerRevealAllSquirrel();
      this.pinContours();
      this.triggerLots();

      this.triggerEnd();
    },

    // pin the map!
    pinMapAndGrid() {
      const targets = [
        '#map-wrapper',
        '#grid-wrapper'
      ]

      targets.forEach(target => {
        ScrollTrigger.create({
          // markers: true,
          trigger: '#map-wrapper',
          endTrigger: '#this-is-a-squirrel',
          start: 'center center',
          end: 'bottom center',
          pin: target,
          invalidateOnRefresh: true,
        });
      });
    },

    // grid bars appear
    triggerMapGridReveal() {
      const configEnter = {
        target: 'grid',
        action: 'draw',
        duration: 4000,
      }

      const configLeave = {
        target: 'grid',
        action: 'clear',
        duration: 4000
      }

      ScrollTrigger.create({
        // markers: true,
        trigger: '#parkHectares',
        endTrigger: '#amPm',
        start: 'top center',
        end: 'bottom center',
        onEnter: () => { this.config = configEnter },
        onLeave: () => { this.config = configLeave },
        onEnterBack: () => { this.config = configEnter },
        onLeaveBack: () => { this.config = configLeave },
      });
    },

    // map cells appear
    triggerMapCells() {
      let counter = {val: 0};
      gsap.to(counter, .75, {
        repeat: -1,
        yoyo: true,
        val: 1,
        onRepeat: () => { 
          this.config = { 'target': 'cells', 'count': counter.val };
        },
        scrollTrigger: {
          // markers: true,
          trigger: '#amPm',
          start: 'top center',
          end: 'bottom center',
          toggleActions: 'play pause play pause'
        }
      });
    },

    // pin all the text before the dots show up
    pinObservationsAndDescriptions() {
      const configs = [{
        trigger: '#observations',
        selector: '#observations > .handwritten'
      }, {
        trigger: '#descriptions',
        selector: '#descriptions > .handwritten'
      }, {
        trigger: '#first-squirrel-description',
        selector: '#first-squirrel-description > p'
      }];

      configs.forEach(config => {
        let tl = gsap.timeline({
          scrollTrigger: {
            // markers: true,
            trigger: config.trigger,
            start: 'center center',
            end: 'bottom top',
            pin: true,
            pinSpacing: true,
            toggleActions: 'play none none reverse'
          }
        });

        const els = document.querySelectorAll(config.selector);
        els.forEach(el => {
          tl.to(el, {opacity: 1, duration: 1})
        })
      })
    },

    pinThisIsASquirrel() {
      let tl = gsap.timeline({
        scrollTrigger: {
          // markers: true,
          trigger: '#this-is-a-squirrel',
          endTrigger: '#first-squirrel-draw-end',
          start: 'center 45%',
          end: 'bottom top',
          pin: true,
          pinSpacing: true,
          toggleActions: 'play none none reverse'
        }
      });

      const els = document.querySelectorAll('#this-is-a-squirrel > .handwritten');
      els.forEach(el => {
        tl.to(el, {opacity: 1, duration: 1})
      })
    },

    // reveal first squirrel
    triggerRevealFirstSquirrel() {
      const chartWrapper = document.querySelector('#chart-wrapper');
      const contourWrapper = document.querySelector('#contour-wrapper')

      ScrollTrigger.create({
        // markers: true,
        trigger: '#this-is-a-squirrel',
        endTrigger: '#first-squirrel-draw-end',
        start: 'top center',
        end: 'bottom top',
        onEnter: () => { 
          chartWrapper.style.visibility = 'visible';
          contourWrapper.style.visibility = 'visible';
          this.config = { target: 'chart', layout:'map', action:'first', duration:0};
        },
        onEnterBack: () => { this.config = {target: 'chart', layout:'map', action:'first', duration:0}},
        onLeaveBack: () => { 
          chartWrapper.style.visibility = 'hidden';
          contourWrapper.style.visibility = 'hidden';
          this.config = {target:'chart', action:'clear', duration:0 } ;
        }
      });
    },

    // reveal all squirrels
    triggerRevealAllSquirrel() {
      ScrollTrigger.create({
        // markers: true,
        trigger: '#all3000',
        start: 'top center',
        end: 'bottom center',
        onEnter: () => { this.config = { target:'chart', layout:'map', color:'gray', action:'reveal', duration:5000 } 
        },
      });
    },

    // pin contour map
    pinContours() {
      const fadeIn = { target:'contour', action:'fadeIn', duration:1500 };
      const fadeOut  = { target:'contour', action:'fadeOut', duration:2001 };
      const fadeBackIn = { target:'contour', action:'fadeIn', duration:1501 };
      const fadeBackOut  = { target:'contour', action:'fadeOut', duration:2000 };


      ScrollTrigger.create({
        // markers: true,
        trigger: '#contourMap',
        start: 'top 40%',
        end: 'bottom 60%',
        onEnter: () => { 
          // this.config = chartOut;
          this.config = fadeIn;
        },
        onLeave: () => { 
          // this.config = chartIn;
          this.config = fadeOut; 
        },
        onEnterBack: () => { 
          // this.config = chartOut;
          this.config = fadeBackIn;
        },
        onLeaveBack: () => { 
          // this.config = chartIn;        
          this.config = fadeBackOut
        },
      });
    },

    triggerLots() {
      const pinArr = [{
        trigger: '#colorMap',
        config: {target:'chart', layout:'map', color:'fur'}
      }, {
        trigger: '#colorGrid',
        config: {target:'chart', layout:'grid', color:'fur', sort:'color'}
      }, {
        trigger: '#ageGrid',
        config: {target:'chart', layout:'grid', color:'age', sort:'age'}
      }, {
        trigger: '#locationGrid',
        config: {target:'chart', layout: 'grid', color:'lowest', sort:'height'}
      }, {
        trigger: '#heightLowest',
        config: {target:'chart', layout:'height', color:'lowest', action:'height'}
      }, {
        trigger: '#heightHighest',
        config: {target:'chart', layout:'height', color:'highest', action:'height'}
      }, {
        trigger: '#heightMap',
        config: {target:'chart', layout:'map', color:'gray'}
      }];


      pinArr.forEach(pin => {
        ScrollTrigger.create({
          // markers: true,
          trigger: pin.trigger,
          start: 'top center',
          end: 'bottom center',
          onEnter: () => { this.config = pin.config },
          onEnterBack: () => { this.config = pin.config }
        }); 
      });
    },

    triggerEnd() {
      const chartCanvas = document.querySelector('#chartCanvas');
      const contourCanvas = document.querySelector('#contourCanvas');
      const wrapper = document.querySelector('.viz-wrapper');
      const els = [chartCanvas, contourCanvas, wrapper];

      ScrollTrigger.create({
        // markers: true,
        trigger: '#end',
        start: 'center center',
        onEnter: () => {
          // hide canvas
          els.forEach(el => {
            el.style.visibility = 'hidden';
          });
        },
        onLeaveBack: () => {
          // show canvas
          els.forEach(el => {
            el.style.visibility = 'visible';
          });
        }
      })
    }
  }
}
</script>
